import PLAGES from '../constants/npa-cantons.json';

/**
 * Le canton d'une société suisse, d'après son code postal.
 *
 * <p>« Si c'est une société avec une adresse de Zurich, il doit se mettre
 * automatiquement avec les taux de Zurich. » Les cotisations qui changent
 * d'un canton à l'autre — allocations familiales et contributions cantonales —
 * se règlent sur le canton de la société. La table des plages de codes
 * postaux est celle du serveur ({@code fiscal/npa-cantons.json}) ; un code
 * postal partagé entre deux cantons y prend celui de la plupart de ses
 * localités, et le patron peut toujours choisir son canton dans les réglages.
 */

/** Les 26 cantons, par leur abréviation officielle. */
export const CANTONS = Object.freeze({
  AG: 'Argovie',
  AI: 'Appenzell Rhodes-Intérieures',
  AR: 'Appenzell Rhodes-Extérieures',
  BE: 'Berne',
  BL: 'Bâle-Campagne',
  BS: 'Bâle-Ville',
  FR: 'Fribourg',
  GE: 'Genève',
  GL: 'Glaris',
  GR: 'Grisons',
  JU: 'Jura',
  LU: 'Lucerne',
  NE: 'Neuchâtel',
  NW: 'Nidwald',
  OW: 'Obwald',
  SG: 'Saint-Gall',
  SH: 'Schaffhouse',
  SO: 'Soleure',
  SZ: 'Schwytz',
  TG: 'Thurgovie',
  TI: 'Tessin',
  UR: 'Uri',
  VD: 'Vaud',
  VS: 'Valais',
  ZG: 'Zoug',
  ZH: 'Zurich',
});

/**
 * Le canton d'un code postal suisse.
 *
 * @param {string|number} [npa] code postal, seul ou suivi de la localité (« 1205 Genève »)
 * @returns {?string} l'abréviation du canton, nulle si le code est inconnu
 */
export function cantonDuNpa(npa) {
  const m = String(npa ?? '').match(/\b(\d{4})\b/);
  if (!m) return null;
  const n = Number(m[1]);
  // Les plages sont triées : une recherche dichotomique suffit.
  let bas = 0;
  let haut = PLAGES.length - 1;
  while (bas <= haut) {
    const milieu = (bas + haut) >> 1;
    const [de, a, canton] = PLAGES[milieu];
    if (n < de) haut = milieu - 1;
    else if (n > a) bas = milieu + 1;
    else return canton;
  }
  return null;
}

/**
 * Le canton de la société : celui qu'elle a choisi dans les réglages, sinon
 * celui de son code postal. Une société hors de Suisse n'a pas de canton.
 *
 * @param {object} [soc] société
 * @returns {?string} l'abréviation du canton
 */
export function cantonDeLaSociete(soc) {
  if ((soc?.pays || 'CH') !== 'CH') return null;
  if (soc?.canton && CANTONS[soc.canton]) return soc.canton;
  return cantonDuNpa(soc?.npa);
}
